import { useCustomBrandingAssetStore } from "./useCustomBrandingAsset";
import { useTranslation } from "react-i18next";
import { useLayoutEffect, useState } from "react";
import {
    EMPTY_LOGO_GIF,
    getCustomBrandingResourceCdnPath,
    initializeCustomBrandingTranslations,
} from "@utils/customBrandingUtils";
import classNames from "classnames";
import { ClassName } from "@constants/styles";
import { CustomBrandingCdnResource } from "@customTypes/customBranding";
import { checkImageUrlExists } from "@utils/networkUtils";
import wsbFavicon from "@resources/WSB_favicon.ico";
import { applyMode, Mode } from "@cloudscape-design/global-styles";
import { ColorTheme } from "@constants/common";
import { getCustomBrandingConfig } from "@utils/configurationUtils";
import { MetricName } from "@constants/logging";
import { useGetLogger } from "@hooks/useGetLogger";

// TODO [LOWA-30534] (Clean up this comment after merging a CR) - Simply moved from `useAppInitialization` hook.
export const useInitializeCustomBranding = () => {
    const setBrandLogoPath = useCustomBrandingAssetStore(
        (store) => store.setBrandLogoPath
    );
    const setHasTermsOfService = useCustomBrandingAssetStore(
        (store) => store.setHasTermsOfService
    );
    const setCustomFontBasedCloudscapeTheme = useCustomBrandingAssetStore(
        (store) => store.setCustomFontBasedCloudscapeTheme
    );
    const [isResourceReady, setIsResourceReady] = useState(false);
    const { t } = useTranslation();
    const log = useGetLogger();

    const browserTabTitle = t("branding.browserTabTitle", {
        defaultValue: "WorkSpaces Secure Browser",
    });

    const customBrandingConfig = getCustomBrandingConfig();

    useLayoutEffect(() => {
        document.title = browserTabTitle;
    }, [browserTabTitle]);

    useLayoutEffect(() => {
        const initializeCustomBrandingAsset = async () => {
            const {
                userSettingId,
                wallpaperExtension,
                faviconExtension,
                logoExtension,
                colorTheme,
                isTermsOfServiceProvided,
            } = customBrandingConfig ?? {};

            if (
                customBrandingConfig?.userSettingId &&
                customBrandingConfig.localizedStrings
            ) {
                initializeCustomBrandingTranslations();
            }

            // Apply custom background class when custom branding is configured
            // regardless of whether wallpaper is provided (will use fallback gradient if no wallpaper)
            document.documentElement.className = classNames(
                document.documentElement.className,
                {
                    [ClassName.CUSTOM_BACKGROUND_IMG]: !!userSettingId,
                }
            );

            // Wallpaper (background image)
            if (userSettingId && wallpaperExtension) {
                const customWallpaperUrl = getCustomBrandingResourceCdnPath(
                    CustomBrandingCdnResource.WALLPAPER,
                    wallpaperExtension
                );
                if (await checkImageUrlExists(customWallpaperUrl)) {
                    document.documentElement.style.setProperty(
                        "--custom-branding-bg-img",
                        `url(${customWallpaperUrl})`
                    );
                    log.publishMetricWithDimensions(
                        MetricName.CUSTOM_BRANDING_ASSET_SUCCESS,
                        {
                            resource: CustomBrandingCdnResource.WALLPAPER,
                        }
                    );
                } else {
                    log.publishMetricWithDimensions(
                        MetricName.MISSING_CUSTOM_BRANDING_ASSET,
                        {
                            resource: CustomBrandingCdnResource.WALLPAPER,
                        }
                    );
                }
            }

            // Favicon
            const faviconElement: HTMLLinkElement | null = document.querySelector(
                'link[rel="icon"]'
            );
            if (faviconElement) {
                if (!userSettingId || !faviconExtension) {
                    faviconElement.href = wsbFavicon;
                } else {
                    const faviconUrl = getCustomBrandingResourceCdnPath(
                        CustomBrandingCdnResource.FAVICON,
                        faviconExtension
                    );
                    if (await checkImageUrlExists(faviconUrl)) {
                        faviconElement.href = faviconUrl;
                        log.publishMetricWithDimensions(
                            MetricName.CUSTOM_BRANDING_ASSET_SUCCESS,
                            {
                                resource: CustomBrandingCdnResource.FAVICON,
                            }
                        );
                    } else {
                        log.publishMetricWithDimensions(
                            MetricName.MISSING_CUSTOM_BRANDING_ASSET,
                            {
                                resource: CustomBrandingCdnResource.FAVICON,
                            }
                        );
                    }
                }
            }

            // Font theme
            const isLightTheme = colorTheme === ColorTheme.LIGHT;
            const isDarkTheme = colorTheme === ColorTheme.DARK;

            document.documentElement.className = classNames(
                document.documentElement.className,
                {
                    [ClassName.CUSTOM_LIGHT_FONT_THEME]: isLightTheme,
                    [ClassName.CUSTOM_DARK_FONT_THEME]: isDarkTheme,
                }
            );
            if (isLightTheme) {
                applyMode(Mode.Light);
                setCustomFontBasedCloudscapeTheme(Mode.Light);
            } else if (isDarkTheme) {
                applyMode(Mode.Dark);
                setCustomFontBasedCloudscapeTheme(Mode.Dark);
            }

            // Logo
            if (!userSettingId || !logoExtension) {
                // Will set WSB logo based on end users' device type
                setBrandLogoPath("");
            } else {
                const logoPath = getCustomBrandingResourceCdnPath(
                    CustomBrandingCdnResource.LOGO,
                    logoExtension
                );
                if (await checkImageUrlExists(logoPath)) {
                    setBrandLogoPath(logoPath);
                    log.publishMetricWithDimensions(
                        MetricName.CUSTOM_BRANDING_ASSET_SUCCESS,
                        {
                            resource: CustomBrandingCdnResource.LOGO,
                        }
                    );
                } else {
                    setBrandLogoPath(EMPTY_LOGO_GIF);
                    log.publishMetricWithDimensions(
                        MetricName.MISSING_CUSTOM_BRANDING_ASSET,
                        {
                            resource: CustomBrandingCdnResource.LOGO,
                        }
                    );
                }
            }

            setHasTermsOfService(isTermsOfServiceProvided ?? false);

            setIsResourceReady(true);
        };
        initializeCustomBrandingAsset();
    }, [customBrandingConfig?.userSettingId]);

    return isResourceReady;
};
